import { type HTMLAttributes } from 'react';
import { cn } from '@/utils/cn';
import { PageGrid } from '../PageGrid';
import { PageColumn } from '../PageColumn';

/**
 * PageLayout — composed `PageGrid` + `PageColumn`.
 *
 * The convenience wrapper for the most common single-column page layout.
 * Renders `col-span-12 w-full grid-primer` → responsive centred column.
 *
 * **No edge margins are applied by default.** Pass `gridProps={{ className: 'px-5' }}`
 * when the layout needs the standard 24px edge margins (e.g. pre-auth pages inside
 * `PreAuthLayout`). Authenticated pages already inherit `px-5` from
 * `AppShell <main>`, so they do not need to pass it here.
 *
 * Use `className` to add per-screen vertical padding on the inner column
 * (e.g. `className="py-8"`). Grid-level overrides go on the outer wrapper
 * via `gridProps`.
 *
 * For multi-column layouts or pages that need a grid without the column
 * constraint, use `PageGrid` and `PageColumn` directly.
 *
 * @example
 * // Authenticated page (AppShell provides px-5)
 * <PageLayout className="py-8">
 *   <Card>...</Card>
 * </PageLayout>
 *
 * @example
 * // Pre-auth page (no parent padding — add px-5 via gridProps)
 * <PageLayout className="py-8" gridProps={{ className: 'px-5' }}>
 *   <Card>...</Card>
 * </PageLayout>
 */
export interface PageLayoutProps extends HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
  /** Props forwarded to the outer `PageGrid` wrapper. Supports `data-*` attributes. */
  gridProps?: HTMLAttributes<HTMLDivElement> & { [key: `data-${string}`]: string | undefined };
}

export function PageLayout({ className, children, gridProps, ...props }: PageLayoutProps) {
  return (
    <PageGrid {...gridProps} className={cn(gridProps?.className)}>
      <PageColumn className={className} {...props}>
        {children}
      </PageColumn>
    </PageGrid>
  );
}

export default PageLayout;
